'use client';

import { Button } from '@paction-2.0/ui';
import { AuthFormInput } from '@/features/auth/components/AuthFormInput/AuthFormInput';
import { SmsConsentNotice } from '@/features/auth/components/SmsConsentNotice/SmsConsentNotice';
import {
  useInlineAccountCompletion,
  type UseInlineAccountCompletionProps
} from './useInlineAccountCompletion';
import styles from './inlineAccountCompletion.module.scss';

export type InlineAccountCompletionProps = UseInlineAccountCompletionProps;

export function InlineAccountCompletion(props: InlineAccountCompletionProps) {
  const {
    loadingContext,
    isNewSignup,
    phone,
    setPhone,
    code,
    setCode,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    firstNameInput,
    setFirstNameInput,
    lastNameInput,
    setLastNameInput,
    sendingCode,
    submitting,
    error,
    codeMessage,
    codeRequested,
    verificationSkipped,
    passwordMismatch,
    canSendCode,
    canSubmit,
    handleSendCode,
    handleSubmit
  } = useInlineAccountCompletion(props);

  if (loadingContext) {
    return (
      <div className={styles.wrapper}>
        <p className={styles.loading}>Loading…</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.wrapper}>
      <h3 className={styles.heading}>Complete your account</h3>

      {isNewSignup && (
        <div className={styles.row}>
          <AuthFormInput
            type="text"
            autoComplete="given-name"
            name="firstName"
            placeholder="First name"
            value={firstNameInput}
            onChange={(e) => setFirstNameInput(e.target.value)}
          />
          <AuthFormInput
            type="text"
            autoComplete="family-name"
            name="lastName"
            placeholder="Last name"
            value={lastNameInput}
            onChange={(e) => setLastNameInput(e.target.value)}
          />
        </div>
      )}

      <div className={styles.field}>
        <AuthFormInput
          type="tel"
          autoComplete="tel"
          name="phone"
          placeholder="Mobile number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <span className={styles.fieldHint}>
          Get a text when a breeder replies, so you don&apos;t miss them.
        </span>
        <SmsConsentNotice className={styles.smsConsent} />
      </div>

      <div className={styles.codeRow}>
        <Button
          type="button"
          onClick={handleSendCode}
          disabled={!canSendCode || sendingCode || verificationSkipped}
          variant="secondary"
        >
          {sendingCode
            ? 'Sending…'
            : codeRequested
              ? 'Resend code'
              : 'Send code'}
        </Button>
        {codeMessage && (
          <span className={styles.codeMessage}>{codeMessage}</span>
        )}
      </div>

      {!verificationSkipped && (
        <div className={styles.field}>
          <AuthFormInput
            type="text"
            name="verificationCode"
            placeholder="Verification code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode="numeric"
          />
        </div>
      )}

      <div className={styles.field}>
        <AuthFormInput
          type="password"
          autoComplete="new-password"
          name="password"
          placeholder="Create password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <div className={styles.field}>
        <AuthFormInput
          type="password"
          autoComplete="new-password"
          name="confirmPassword"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={passwordMismatch}
          errorText={passwordMismatch ? 'Passwords must match' : undefined}
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}

      <Button
        type="submit"
        disabled={!canSubmit || submitting}
        className={styles.submitButton}
      >
        {submitting ? 'Creating account…' : 'Complete sign-up & log in'}
      </Button>
    </form>
  );
}
